function scrollIntoViewAsync(el, options = { behavior: "smooth", block: "start" }) {
  return new Promise((resolve) => {
    if ("onscrollend" in document) {
      const onEnd = () => {
        document.removeEventListener("scrollend", onEnd);
        resolve();
      };
      document.addEventListener("scrollend", onEnd, { once: true });
      el.scrollIntoView(options);
      return;
    }

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          resolve();
        }
      }, { threshold: 1 });
      observer.observe(el);
      el.scrollIntoView(options);
      return;
    }

    el.scrollIntoView(options);
    setTimeout(resolve, 500);
  });
}

class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.selectedOption = null;
    this.options = [];
    this.activeOptionIndex = -1;
    this.openedByKeyboard = false;
    this.init();
  }

  init() {
    this.setupReferences();
    this.bindEvents();
    this.setInitialState();
  }

  setupReferences() {
    // Get references to existing HTML elements
    this.trigger = this.querySelector('.custom-dropdown__trigger');
    this.content = this.querySelector('.custom-dropdown__content');
    this.optionsContainer = this.querySelector('.custom-dropdown__options-container');
    this.label = this.querySelector('.custom-dropdown__label');
    this.arrow = this.querySelector('.custom-dropdown__arrow');
  }

  bindEvents() {
    // Toggle dropdown
    this.trigger.addEventListener('click', () => {
      this.openedByKeyboard = false;
      this.toggle();
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });

    // Keyboard navigation for trigger
    this.trigger.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.openedByKeyboard = true;
          this.toggle();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.openedByKeyboard = true;
          this.open();
          this.focusFirstOption();
          break;
      }
    });

    // Handle Tab/Shift+Tab when dropdown is open
    this.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;

      if (e.key === 'Tab') {
        this.close();
        // Let the default Tab behavior continue
      }
    });

    // Reposition dropdown on window resize and scroll
    window.addEventListener('resize', () => {
      if (this.isOpen) {
        this.positionDropdown();
      }
    });

    window.addEventListener('scroll', () => {
      if (this.isOpen) {
        this.positionDropdown();
      }
    });
  }

  setInitialState() {
    // Check if options are provided via data-options attribute
    const dataOptions = this.getAttribute('data-options');
    if (dataOptions) {
      try {
        const optionsData = JSON.parse(dataOptions);
        this.renderOptionsFromData(optionsData);
      } catch (error) {
        console.error('Error parsing data-options:', error);
      }
    }

    // Get options from DOM elements
    this.options = this.getOptionsFromDOM();
    this.bindOptionEvents();

    // Set initial selected option if one exists
    const selectedOption = this.options.find(option => option.selected);
    if (selectedOption) {
      this.selectedOption = selectedOption;
      this.label.textContent = selectedOption.label;

      // Update the selected option element
      if (selectedOption.element) {
        selectedOption.element.setAttribute('aria-selected', 'true');
        selectedOption.element.classList.add('custom-dropdown__option--selected');
      }
    }
  }

  getOptionsFromDOM() {
    const optionElements = this.optionsContainer.querySelectorAll('.custom-dropdown__option');
    const options = [];

    optionElements.forEach((element, index) => {
      // Add disabled class if aria-disabled is true
      if (element.getAttribute('aria-disabled') === 'true') {
        element.classList.add('custom-dropdown__option--disabled');
      }

      options.push({
        value: element.getAttribute('data-value'),
        label: element.textContent.trim(),
        selected: element.getAttribute('aria-selected') === 'true',
        disabled: element.hasAttribute('aria-disabled'),
        element: element,
        index: index
      });
    });



    return options;
  }

  bindOptionEvents() {
    this.options.forEach((option, index) => {
      const optionElement = option.element;

      // Make options focusable (disabled options get tabindex -1)
      if (option.disabled) {
        optionElement.setAttribute('tabindex', '-1');
      } else {
        optionElement.setAttribute('tabindex', '0');
      }
      optionElement.setAttribute('role', 'option');

      // Add event listeners
      optionElement.addEventListener('click', () => this.selectOption(option));
      optionElement.addEventListener('keydown', (e) => this.handleOptionKeydown(e, index));
      optionElement.addEventListener('focus', () => this.setActiveOption(index));
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.content.hidden = false;
    this.classList.add('custom-dropdown--open');

    // Set active option to current selection or first option
    if (this.selectedOption) {
      this.activeOptionIndex = this.selectedOption.index;
    } else {
      this.activeOptionIndex = 0;
    }

    this.updateActiveOption();
    this.positionDropdown();

    // Focus the first option when dropdown opens
    this.focusFirstOption();
  }

  close() {
    this.isOpen = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.content.hidden = true;
    this.classList.remove('custom-dropdown--open');
    this.activeOptionIndex = -1;
    this.updateActiveOption();

    // Only return focus to trigger if opened by keyboard
    if (this.openedByKeyboard) {
      this.trigger.focus();
    }
    this.openedByKeyboard = false;
  }

  selectOption(option) {
    // Check if option is disabled via aria-disabled attribute
    if (option.element && option.element.getAttribute('aria-disabled') === 'true') {
      return; // Don't select disabled options
    }

    this.selectedOption = option;
    this.label.textContent = option.label;

    // Update selected state in options
    this.optionsContainer.querySelectorAll('.custom-dropdown__option').forEach(opt => {
      opt.setAttribute('aria-selected', 'false');
      opt.classList.remove('custom-dropdown__option--selected');
    });

    // Update the selected option element
    if (option.element) {
      option.element.setAttribute('aria-selected', 'true');
      option.element.classList.add('custom-dropdown__option--selected');
    }

    // Close dropdown immediately after selection
    this.close();

    // Dispatch change event with more detailed information
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        value: option.value,
        option: option,
        label: option.label,
        selected: true
      }
    }));
  }

  setValue(value) {
    const option = this.options.find(opt => opt.value === value);
    if (option) {
      this.selectOption(option);
    }
  }

  getValue() {
    return this.selectedOption ? this.selectedOption.value : null;
  }

  async focus() {
    this.trigger.focus();

    // Check if element is already in viewport
    const rect = this.getBoundingClientRect();
    const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (isInViewport) {
      this.open();
      return;
    }

    // Use the global utility to scroll and wait for completion
    await scrollIntoViewAsync(this, { behavior: 'smooth', block: 'center' });
    this.open();
  }

  focusFirstOption() {
    const firstOption = this.optionsContainer.querySelector('.custom-dropdown__option[tabindex="0"]');
    if (firstOption) {
      firstOption.focus();
    }
  }

  focusLastOption() {
    const options = Array.from(this.optionsContainer.querySelectorAll('.custom-dropdown__option[tabindex="0"]'));
    if (options.length > 0) {
      options[options.length - 1].focus();
    }
  }

  setActiveOption(index) {
    this.activeOptionIndex = index;
    this.updateActiveOption();
  }

  updateActiveOption() {
    // Remove active state from all options
    this.optionsContainer.querySelectorAll('.custom-dropdown__option').forEach(option => {
      option.setAttribute('aria-selected', 'false');
      option.classList.remove('custom-dropdown__option--active');
    });

    // Set active state on current active option
    if (this.activeOptionIndex >= 0 && this.activeOptionIndex < this.options.length) {
      const activeOption = this.options[this.activeOptionIndex];
      if (activeOption && activeOption.element) {
        activeOption.element.setAttribute('aria-selected', 'true');
        activeOption.element.classList.add('custom-dropdown__option--active');
      }
    }
  }

  positionDropdown() {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownRect = this.getBoundingClientRect();

    // Reset any previous positioning
    this.content.style.position = '';
    this.content.style.top = '';
    this.content.style.bottom = '';
    this.content.style.left = '';
    this.content.style.maxHeight = '';
    this.content.style.transform = '';

    // Calculate available space
    const spaceBelow = viewportHeight - dropdownRect.bottom;
    const spaceAbove = dropdownRect.top;

    // Get the height of a single option to calculate minimum space needed
    const firstOption = this.optionsContainer.querySelector('.custom-dropdown__option');
    const optionHeight = firstOption ? firstOption.offsetHeight : 50; // Default 50px if no options
    const minOptionsToShow = 2; // Minimum number of options to show before switching to upward
    const minSpaceNeeded = optionHeight * minOptionsToShow + 20; // 20px for padding

    // Try to open downward first, but ensure at least 2 options can fit
    let shouldOpenUp = false;
    let maxHeight = 300; // Default max height

    if (spaceBelow >= minSpaceNeeded) {
      // Can fit at least 2 options below, open downward
      shouldOpenUp = false;
      maxHeight = Math.min(spaceBelow - 10, 300);
    } else if (spaceAbove >= minSpaceNeeded) {
      // Can fit at least 2 options above, open upward
      shouldOpenUp = true;
      maxHeight = Math.min(spaceAbove - 10, 300);
    } else {
      // Neither direction can fit 2 options, choose the direction with more space
      shouldOpenUp = spaceAbove > spaceBelow;
      maxHeight = Math.min(Math.max(spaceBelow, spaceAbove) - 10, 300);
    }

    if (shouldOpenUp) {
      // Position above the trigger
      this.content.style.position = 'absolute';
      this.content.style.bottom = '100%';
      this.content.style.top = 'auto';
      this.content.style.left = '0';
      this.content.style.maxHeight = `${maxHeight}px`;
    } else {
      // Position below the trigger (default)
      this.content.style.position = 'absolute';
      this.content.style.top = '100%';
      this.content.style.bottom = 'auto';
      this.content.style.left = '0';
      this.content.style.maxHeight = `${maxHeight}px`;
    }

    // Handle horizontal overflow
    const contentWidth = this.content.offsetWidth;
    const rightOverflow = dropdownRect.left + contentWidth - viewportWidth;

    if (rightOverflow > 0) {
      // Shift left to prevent overflow
      this.content.style.left = `${-rightOverflow - 10}px`;
    }

    // Ensure minimum left position
    const leftPosition = parseInt(this.content.style.left) || 0;
    if (leftPosition < -dropdownRect.left + 10) {
      this.content.style.left = `${-dropdownRect.left + 10}px`;
    }
  }

  moveToNextOption() {
    const enabledOptions = this.options.filter(option => !option.disabled);
    if (enabledOptions.length === 0) return;

    let currentIndex = enabledOptions.findIndex(option => option.index === this.activeOptionIndex);
    if (currentIndex === -1) currentIndex = 0;

    const nextIndex = (currentIndex + 1) % enabledOptions.length;
    const nextOption = enabledOptions[nextIndex];


    this.setActiveOption(nextOption.index);
    nextOption.element.focus();
  }

  moveToPreviousOption() {
    const enabledOptions = this.options.filter(option => !option.disabled);
    if (enabledOptions.length === 0) return;

    let currentIndex = enabledOptions.findIndex(option => option.index === this.activeOptionIndex);
    if (currentIndex === -1) currentIndex = 0;

    const prevIndex = currentIndex === 0 ? enabledOptions.length - 1 : currentIndex - 1;
    const prevOption = enabledOptions[prevIndex];


    this.setActiveOption(prevOption.index);
    prevOption.element.focus();
  }

  selectActiveOption() {
    if (this.activeOptionIndex >= 0 && this.activeOptionIndex < this.options.length) {
      const activeOption = this.options[this.activeOptionIndex];
      if (activeOption && !activeOption.disabled) {
        this.selectOption(activeOption);
      }
    }
  }

  handleOptionKeydown(e, index) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectActiveOption();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.moveToNextOption();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.moveToPreviousOption();
        break;
      case 'Home':
        e.preventDefault();
        const firstOption = this.options.find(opt => !opt.disabled);
        if (firstOption) {
          this.setActiveOption(firstOption.index);
          firstOption.element.focus();
        }
        break;
      case 'End':
        e.preventDefault();
        const enabledOptions = this.options.filter(opt => !opt.disabled);
        if (enabledOptions.length > 0) {
          const lastOption = enabledOptions[enabledOptions.length - 1];
          this.setActiveOption(lastOption.index);
          lastOption.element.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.openedByKeyboard = true;
        this.close();
        // Focus will be returned to trigger in close() method
        break;
    }
  }


}

// Register the custom element
customElements.define('custom-dropdown', CustomDropdown);
